/**
 * Pick a show style (D32): a select over the tenant's show styles, the
 * studio's default marked. `value` is what is shown selected — the container
 * resolves it (`lib/showStyle/pick.ts`: the pick, else the studio's default,
 * else the first). `allowNone` adds a "(none)" choice with the value ''.
 */
import { Field, FieldDescription } from '../ui/field.js';
import { Label } from '../ui/label.js';
import { NativeSelect } from '../ui/native-select.js';

export type ShowStyleChoice = { readonly id: string; readonly name: string };

export type ShowStylePickerProps = {
  /** The select's id (its label's `htmlFor`). */
  readonly id: string;
  readonly label: string;
  readonly showStyles: readonly ShowStyleChoice[];
  readonly value: string;
  /** The studio's default show style, marked in the list; null for none. */
  readonly studioDefault: string | null;
  readonly allowNone: boolean;
  readonly disabled: boolean;
  readonly description: string | null;
  readonly onChange: (showStyleId: string) => void;
};

export function ShowStylePicker({ id, label, showStyles, value, studioDefault, allowNone, disabled, description, onChange }: ShowStylePickerProps) {
  const none = showStyles.length === 0;
  return (
    <Field>
      <Label htmlFor={id}>{label}</Label>
      <NativeSelect id={id} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled || (none && !allowNone)} data-testid={`${id}-select`}>
        {allowNone ? <option value="">(none)</option> : null}
        {none && !allowNone ? <option value="">No show styles — create one first</option> : null}
        {showStyles.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
            {s.id === studioDefault ? ' (studio default)' : ''}
          </option>
        ))}
      </NativeSelect>
      {description === null ? null : <FieldDescription>{description}</FieldDescription>}
    </Field>
  );
}
